import { useState, useEffect } from 'react';
import { useToast } from '../../contexts/ToastContext';
import api from '../../services/api';
import { FaFolder, FaFile, FaChevronDown, FaChevronRight, FaDownload } from 'react-icons/fa';
import { ChevronDown as ChevronDownLucide } from 'lucide-react';

interface Tenant {
  legal_name: string;
  uuid: string;
}

interface ApiKey {
  name: string;
  uuid: string;
}

interface Bucket {
  name: string;
}

interface ObjectInfo {
  content_size: number;
  key: string;
  last_modified: string;
}

interface ObjectListResponse {
  folders: string[];
  files: ObjectInfo[];
}

interface FolderProps {
  path: string;
  name: string;
  level: number;
}

// Função utilitária para formatar o tamanho do arquivo
const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

// Função para normalizar o caminho removendo barras duplas
const normalizePath = (path: string) => {
  return path.replace(/\/+/g, '/');
};

// Função utilitária para formatar a data
const formatDate = (dateString: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Função utilitária para download de objeto
const downloadObject = async (key: string, bucket: string | undefined, addToast: any) => {
  if (!bucket) {
    addToast({ type: 'error', title: 'Bucket não selecionado', description: 'Selecione um bucket para baixar o arquivo.' });
    return;
  }
  try {
    const response = await api.post(
      '/api/mgc/object-storage/objects/download',
      { key, bucket },
      { responseType: 'blob' }
    );
    const fileName = key.split('/').pop() || 'downloaded-file';
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    addToast({
      type: 'error',
      title: 'Erro ao baixar arquivo',
      description: error?.response?.data?.error || error.message
    });
  }
};

// Componente recursivo para exibir pastas
function Folder({ path, name, level, selectedBucket }: FolderProps & { selectedBucket: string | undefined }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [contents, setContents] = useState<ObjectListResponse | null>(null);
  const { addToast } = useToast();

  // Extrai o nome da pasta para exibição
  const displayName = name.endsWith('/') 
    ? name.slice(0, -1).split('/').pop() 
    : name.split('/').pop();
  
  // Pega o nome do bucket (primeira parte do path)
  const bucketName = path.split('/')[0];
  
  // No primeiro nível usamos path + name, nos outros níveis garantimos que o bucket esteja no início
  const requestPath = level === 0 
    ? `${path}/${name}`
    : name.startsWith(bucketName) 
      ? name 
      : `${bucketName}/${name}`;

  const loadContents = async () => {
    try {
      setIsLoading(true);
      console.log('Carregando conteúdo do caminho:', requestPath); // Log para debug
      const response = await api.post('/api/mgc/object-storage/objects', {
        path: requestPath
      });
      setContents(response.data.data);
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro ao carregar conteúdo da pasta',
        description: error.message
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = () => {
    if (!isOpen && !contents) {
      loadContents();
    }
    setIsOpen(!isOpen);
  };

  return (
    <div style={{ marginLeft: `${level * 20}px` }}>
      <div
        className="flex items-center space-x-2 py-2 px-2 hover:bg-gray-100 cursor-pointer"
        onClick={handleToggle}
      >
        <span className="text-gray-500">
          {isOpen ? <FaChevronDown /> : <FaChevronRight />}
        </span>
        <FaFolder className="text-yellow-400" />
        <span className="text-sm">
          {displayName}
        </span>
      </div>
      
      {isOpen && (
        <div>
          {isLoading ? (
            <div className="py-2 px-2 text-sm text-gray-500">Carregando...</div>
          ) : contents ? (
            <>
              {/* Renderiza as subpastas recursivamente */}
              {contents.folders.map((folderName) => (
                <Folder
                  key={folderName}
                  path={path}
                  name={folderName}
                  level={level + 1}
                  selectedBucket={selectedBucket}
                />
              ))}
              
              {/* Renderiza os arquivos */}
              {contents.files.map((file) => (
                <div
                  key={file.key}
                  className="flex items-center space-x-2 py-2 px-2 hover:bg-gray-100"
                  style={{ marginLeft: `${(level + 1) * 20}px` }}
                >
                  <FaFile className="text-gray-400" />
                  <span className="text-sm flex-1">{file.key}</span>
                  <span className="text-xs text-gray-500 mr-4">
                    {formatDate(file.last_modified)}
                  </span>
                  <span className="text-xs text-gray-500">
                    {formatFileSize(file.content_size)}
                  </span>
                  <button
                    className="ml-2 px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-1"
                    title="Baixar arquivo"
                    onClick={() => downloadObject(file.key, selectedBucket, addToast)}
                  >
                    <FaDownload />
                  </button>
                </div>
              ))}
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}

export function BucketList() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [isLoadingTenants, setIsLoadingTenants] = useState(false);
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [selectedApiKey, setSelectedApiKey] = useState<ApiKey | null>(null);
  const [isLoadingApiKeys, setIsLoadingApiKeys] = useState(false);
  const [buckets, setBuckets] = useState<Bucket[]>([]);
  const [selectedBucket, setSelectedBucket] = useState<Bucket | null>(null);
  const [isLoadingBuckets, setIsLoadingBuckets] = useState(false);
  const [rootContents, setRootContents] = useState<ObjectListResponse | null>(null);
  const [isLoadingContents, setIsLoadingContents] = useState(false);
  const { addToast } = useToast();

  // Carregar tenants ao montar o componente
  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      setIsLoadingTenants(true);
      const response = await api.get('api/mgc/tenants');
      setTenants(response.data.data);
      
      if (response.data.data.length === 0) {
        addToast({
          type: 'info',
          title: 'Nenhum tenant encontrado',
          description: 'Não foram encontrados tenants disponíveis.'
        });
      }
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro ao carregar tenants',
        description: error.message
      });
    } finally {
      setIsLoadingTenants(false);
    }
  };

  const fetchApiKeys = async () => {
    try {
      setIsLoadingApiKeys(true);
      const response = await api.get('/api/mgc/object-storage/api-key');
      setApiKeys(response.data.data);
      
      if (response.data.data.length === 0) {
        addToast({
          type: 'info',
          title: 'Nenhuma API Key encontrada',
          description: 'Não foram encontradas API Keys disponíveis para este tenant.'
        });
      }
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro ao carregar API Keys',
        description: error.message
      });
    } finally {
      setIsLoadingApiKeys(false);
    }
  };

  const fetchBuckets = async () => {
    try {
      setIsLoadingBuckets(true);
      const response = await api.get('/api/mgc/object-storage/buckets');
      setBuckets(response.data.data);
      
      if (response.data.data.length === 0) {
        addToast({
          type: 'info',
          title: 'Nenhum bucket encontrado',
          description: 'Não foram encontrados buckets disponíveis para esta API Key.'
        });
      }
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro ao carregar buckets',
        description: error.message
      });
    } finally {
      setIsLoadingBuckets(false);
    }
  };

  const handleTenantSelect = async (tenant: Tenant) => {
    try {
      // Se selecionou o mesmo tenant, não faz nada
      if (selectedTenant?.uuid === tenant.uuid) return;

      setSelectedTenant(tenant);
      setSelectedApiKey(null); // Limpa a API Key selecionada
      setApiKeys([]); // Limpa a lista de API Keys
      setBuckets([]); // Limpa a lista de buckets
      setSelectedBucket(null); // Limpa o bucket selecionado
      
      // Setar o tenant selecionado
      await api.put(`/api/mgc/tenants/${tenant.uuid}`);
      
      // Carregar as API Keys após selecionar o tenant
      await fetchApiKeys();
      
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro ao selecionar tenant',
        description: error.message
      });
    }
  };

  const handleApiKeySelect = async (apiKey: ApiKey) => {
    try {
      // Se selecionou a mesma API Key, não faz nada
      if (selectedApiKey?.uuid === apiKey.uuid) return;

      setSelectedApiKey(apiKey);
      setBuckets([]); // Limpa a lista de buckets
      setSelectedBucket(null); // Limpa o bucket selecionado
      
      // Setar a API Key selecionada
      await api.put(`/api/mgc/object-storage/api-key/${apiKey.uuid}`);
      
      // Carregar os buckets após selecionar a API Key
      await fetchBuckets();
      
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro ao selecionar API Key',
        description: error.message
      });
    }
  };

  const handleBucketSelect = async (bucket: Bucket) => {
    setSelectedBucket(bucket);
    setRootContents(null);
    
    try {
      setIsLoadingContents(true);
      const response = await api.post('/api/mgc/object-storage/objects', {
        path: normalizePath(bucket.name)
      });
      setRootContents(response.data.data);
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro ao carregar conteúdo do bucket',
        description: error.message
      });
    } finally {
      setIsLoadingContents(false);
    }
  };

  return (
    <div className="container mx-auto px-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-xl font-bold text-gray-900">Buckets</h1>
      </div>

      {/* Filtros em linha */}
      <div className="bg-white rounded-lg shadow-lg mb-4">
        <div className="p-4">
          <div className="flex gap-4">
            {/* Tenant Select */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">1. Selecione o Tenant</label>
              <div className="relative">
                <select
                  value={selectedTenant?.uuid || ''}
                  onChange={(e) => {
                    const tenant = tenants.find(t => t.uuid === e.target.value);
                    if (tenant) handleTenantSelect(tenant);
                  }}
                  className={`block w-full rounded-md border bg-white py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-1 appearance-none ${
                    isLoadingTenants 
                      ? 'border-gray-300 cursor-wait' 
                      : tenants.length === 0 
                        ? 'border-yellow-300 focus:border-yellow-500 focus:ring-yellow-500'
                        : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                  }`}
                  disabled={isLoadingTenants}
                >
                  <option value="">
                    {isLoadingTenants 
                      ? 'Carregando tenants...'
                      : tenants.length === 0
                        ? 'Nenhum tenant encontrado'
                        : 'Selecione um tenant'
                    }
                  </option>
                  {tenants.map((tenant) => (
                    <option key={tenant.uuid} value={tenant.uuid}>
                      {tenant.legal_name}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  {isLoadingTenants ? (
                    <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                  ) : (
                    <ChevronDownLucide className="h-4 w-4" />
                  )}
                </div>
              </div>
            </div>

            {/* API Key Select */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">2. Selecione a API Key</label>
              <div className="relative">
                <select
                  value={selectedApiKey?.uuid || ''}
                  onChange={(e) => {
                    const apiKey = apiKeys.find(k => k.uuid === e.target.value);
                    if (apiKey) handleApiKeySelect(apiKey);
                  }}
                  className={`block w-full rounded-md border bg-white py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-1 appearance-none ${
                    !selectedTenant
                      ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                      : isLoadingApiKeys
                        ? 'border-gray-300 cursor-wait'
                        : apiKeys.length === 0
                          ? 'border-yellow-300 focus:border-yellow-500 focus:ring-yellow-500'
                          : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                  }`}
                  disabled={!selectedTenant || isLoadingApiKeys}
                >
                  <option value="">
                    {!selectedTenant
                      ? 'Selecione um tenant primeiro'
                      : isLoadingApiKeys
                        ? 'Carregando API Keys...'
                        : apiKeys.length === 0
                          ? 'Nenhuma API Key encontrada'
                          : 'Selecione uma API Key'
                    }
                  </option>
                  {apiKeys.map((apiKey) => (
                    <option key={apiKey.uuid} value={apiKey.uuid}>
                      {apiKey.name}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  {isLoadingApiKeys ? (
                    <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                  ) : (
                    <ChevronDownLucide className="h-4 w-4" />
                  )}
                </div>
              </div>
            </div>

            {/* Bucket Select */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">3. Buckets</label>
              <div className="relative">
                <select
                  value={selectedBucket?.name || ''}
                  onChange={(e) => {
                    const bucket = buckets.find(b => b.name === e.target.value);
                    if (bucket) handleBucketSelect(bucket);
                  }}
                  className={`block w-full rounded-md border bg-white py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-1 appearance-none ${
                    !selectedApiKey
                      ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                      : isLoadingBuckets
                        ? 'border-gray-300 cursor-wait'
                        : buckets.length === 0
                          ? 'border-yellow-300 focus:border-yellow-500 focus:ring-yellow-500'
                          : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                  }`}
                  disabled={!selectedApiKey || isLoadingBuckets}
                >
                  <option value="">
                    {!selectedApiKey
                      ? 'Selecione uma API Key primeiro'
                      : isLoadingBuckets
                        ? 'Carregando buckets...'
                        : buckets.length === 0
                          ? 'Nenhum bucket encontrado'
                          : 'Selecione um bucket'
                    }
                  </option>
                  {buckets.map((bucket) => (
                    <option key={bucket.name} value={bucket.name}>
                      {bucket.name}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  {isLoadingBuckets ? (
                    <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                  ) : (
                    <ChevronDownLucide className="h-4 w-4" />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Conteúdo do Bucket */}
      {selectedBucket && (
        <div className="bg-white rounded-lg shadow-lg">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-base font-semibold text-gray-800">4. Conteúdo do Bucket</h2>
          </div>
          <div className="p-3">
            {isLoadingContents ? (
              <div className="text-center py-2 text-sm">Carregando conteúdo...</div>
            ) : rootContents ? (
              <div className="border rounded-lg">
                {/* Renderiza as pastas do nível raiz */}
                {rootContents.folders.map((folderName) => (
                  <Folder
                    key={folderName}
                    path={selectedBucket.name}
                    name={folderName}
                    level={0}
                    selectedBucket={selectedBucket?.name}
                  />
                ))}
                
                {/* Renderiza os arquivos do nível raiz */}
                {rootContents.files.map((file) => (
                  <div
                    key={file.key}
                    className="flex items-center space-x-2 py-2 px-2 hover:bg-gray-100"
                  >
                    <FaFile className="text-gray-400" />
                    <span className="text-sm flex-1">{file.key}</span>
                    <span className="text-xs text-gray-500 mr-4">
                      {formatDate(file.last_modified)}
                    </span>
                    <span className="text-xs text-gray-500">
                      {formatFileSize(file.content_size)}
                    </span>
                    <button
                      className="ml-2 px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-1"
                      title="Baixar arquivo"
                      onClick={() => downloadObject(file.key, selectedBucket?.name, addToast)}
                    >
                      <FaDownload />
                    </button>
                  </div>
                ))}

                {rootContents.folders.length === 0 && rootContents.files.length === 0 && (
                  <div className="text-center py-4 text-sm text-gray-500">
                    Bucket vazio
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
} 